import { useEffect, useState } from "react";
import { api, formatApiError, formatIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Package, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAutoRefresh } from "../hooks/useAutoRefresh";

export default function SparepartInventory() {
  const { user } = useAuth();
  const isKoor = user?.role === "koordinator";
  const [inventory, setInventory] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ catalog_id: "", quantity: 1, unit_price: "", purchase_date: new Date().toISOString().slice(0, 10), notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/sparepart-inventory").then((r) => setInventory(r.data));
    api.get("/sparepart-purchases").then((r) => setPurchases(r.data));
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load, 30000);

  const openAdd = () => {
    setForm({ catalog_id: "", quantity: 1, unit_price: "", purchase_date: new Date().toISOString().slice(0, 10), notes: "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.catalog_id) { toast.error("Pilih sparepart"); return; }
    setBusy(true);
    try {
      const payload = { catalog_id: form.catalog_id, quantity: Number(form.quantity), purchase_date: form.purchase_date, notes: form.notes };
      if (!isKoor && form.unit_price !== "") payload.unit_price = Number(form.unit_price);
      await api.post("/sparepart-purchases", payload);
      toast.success("Barang masuk stok");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const selectedCat = inventory.find((i) => i.catalog_id === form.catalog_id);

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Stok Sparepart</h1>
          <p className="text-slate-500 text-sm mt-1">
            Inventory sparepart real-time. {isKoor && "Anda dapat catat barang masuk (harga otomatis dari master)."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase tracking-wider rounded-sm btn-industrial h-12" data-testid="add-purchase-btn">
              <Plus className="w-5 h-5 mr-1" /> Barang Masuk
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm border-2 border-slate-900">
            <DialogHeader><DialogTitle className="font-display text-2xl font-black">Terima Sparepart Masuk Stok</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Sparepart *</Label>
                <Select value={form.catalog_id} onValueChange={(v) => setForm({ ...form, catalog_id: v })}>
                  <SelectTrigger className="h-12 rounded-sm" data-testid="purchase-catalog"><SelectValue placeholder="-- Pilih sparepart --" /></SelectTrigger>
                  <SelectContent>
                    {inventory.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">Master sparepart kosong. Hubungi management.</div>
                    ) : (
                      inventory.map((i) => <SelectItem key={i.catalog_id} value={i.catalog_id}>{i.name} ({i.unit})</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Qty {selectedCat && `(${selectedCat.unit})`} *</Label>
                  <Input required type="number" min={0} step="0.1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="h-12 rounded-sm font-mono text-lg" data-testid="purchase-qty" />
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Tanggal *</Label>
                  <Input required type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="h-12 rounded-sm font-mono" data-testid="purchase-date" />
                </div>
              </div>
              {!isKoor && (
                <div>
                  <Label className="uppercase text-xs tracking-widest font-bold">Override Harga per Satuan (opsional)</Label>
                  <Input type="number" min={0} value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="h-12 rounded-sm font-mono" placeholder={selectedCat ? `Default: ${selectedCat.default_price}` : ""} data-testid="purchase-price" />
                </div>
              )}
              <div>
                <Label className="uppercase text-xs tracking-widest font-bold">Catatan (opsional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="h-12 rounded-sm" placeholder="No invoice / supplier" data-testid="purchase-notes" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={busy || !form.catalog_id} data-testid="purchase-submit" className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold uppercase rounded-sm btn-industrial">
                  {busy ? "..." : "Simpan"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden mb-6">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <Package className="w-5 h-5" />
          <div className="font-display font-black text-lg text-slate-900">Stok Saat Ini</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Nama</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Satuan</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Stok</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Total Masuk</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Total Terpakai</TableHead>
              {!isKoor && <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Nilai Stok</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {inventory.map((r) => (
              <TableRow key={r.catalog_id} data-testid={`inv-row-${r.name}`}>
                <TableCell className="font-bold">{r.name}</TableCell>
                <TableCell className="font-mono uppercase text-xs">{r.unit}</TableCell>
                <TableCell className={`text-right font-mono font-bold text-lg ${r.on_hand <= 0 ? "text-red-600" : r.on_hand < 5 ? "text-amber-600" : "text-slate-900"}`}>
                  {r.on_hand} {r.on_hand <= 0 && <AlertTriangle className="w-4 h-4 inline ml-1" />}
                </TableCell>
                <TableCell className="text-right font-mono text-slate-500">{r.total_in}</TableCell>
                <TableCell className="text-right font-mono text-slate-500">{r.total_out}</TableCell>
                {!isKoor && <TableCell className="text-right font-mono">{formatIDR(r.inventory_value)}</TableCell>}
              </TableRow>
            ))}
            {inventory.length === 0 && (
              <TableRow><TableCell colSpan={isKoor ? 5 : 6} className="text-center py-10 text-slate-500">Master sparepart kosong. Management perlu tambah master dulu.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div className="font-display font-black text-lg text-slate-900">Riwayat Barang Masuk</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Tanggal</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Sparepart</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Qty</TableHead>
              {!isKoor && <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Harga / satuan</TableHead>}
              {!isKoor && <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Total</TableHead>}
              <TableHead className="uppercase text-xs tracking-widest font-bold">Diterima oleh</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchases.map((p) => {
              const cat = inventory.find((i) => i.catalog_id === p.catalog_id);
              return (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-sm">{p.purchase_date}</TableCell>
                  <TableCell>{cat?.name || p.catalog_id}</TableCell>
                  <TableCell className="text-right font-mono">{p.quantity} {cat?.unit}</TableCell>
                  {!isKoor && <TableCell className="text-right font-mono">{formatIDR(p.unit_price)}</TableCell>}
                  {!isKoor && <TableCell className="text-right font-mono font-bold">{formatIDR(p.total_cost)}</TableCell>}
                  <TableCell className="text-xs text-slate-500">{p.created_by}</TableCell>
                </TableRow>
              );
            })}
            {purchases.length === 0 && (
              <TableRow><TableCell colSpan={isKoor ? 4 : 6} className="text-center py-8 text-slate-500 text-sm">Belum ada barang masuk.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
